import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSendReport = async () => {
    setEmailSending(true);
    setEmailMessage(null);
    try {
      const res = await api.post('/admin/trigger-report-email');
      setEmailMessage({ type: 'success', text: res.data.message });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to trigger report email';
      setEmailMessage({ type: 'error', text: detail });
    } finally {
      setEmailSending(false);
    }
  };

  const menuItems = [
    {
      title: 'Bulk Upload MRs',
      description: 'Upload Excel file to add multiple MR users at once',
      icon: '📤',
      path: '/admin/bulk-upload',
      color: '#4CAF50'
    },
    {
      title: 'Doctor Search',
      description: 'Search, view, edit, and regenerate doctor profiles',
      icon: '🔍',
      path: '/admin/doctors',
      color: '#2196F3'
    },
    {
      title: 'Export Data',
      description: 'Export PDF with serial numbers or Excel reports',
      icon: '📊',
      path: '/admin/export',
      color: '#FF9800'
    }
  ];

  return (
    <div className="container admin-container" style={{ maxWidth: '800px' }}>
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Welcome, {user?.username}</p>
        </div>
        <button
          onClick={handleLogout}
          className="btn btn-secondary"
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          Logout
        </button>
      </div>

      {/* Menu Cards */}
      <div style={{ marginTop: '32px' }}>
        {menuItems.map((item, index) => (
          <Link
            key={index}
            to={item.path}
            style={{ textDecoration: 'none' }}
          >
            <div 
              className="card" 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                padding: '24px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = item.color;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: `${item.color}20`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                marginRight: '20px'
              }}>
                {item.icon}
              </div>
              <div>
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  color: '#333',
                  marginBottom: '4px'
                }}>
                  {item.title}
                </h3>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  margin: 0
                }}>
                  {item.description}
                </p>
              </div>
              <div style={{ marginLeft: 'auto', color: '#999', fontSize: '20px' }}>
                →
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Send Report Email */}
      <div className="card" style={{ marginTop: '24px', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#e8f5e9',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            marginRight: '16px'
          }}>
            ✉️
          </div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '2px' }}>
              Send Report Email
            </h3>
            <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
              Generate Excel report and email it to all configured recipients
            </p>
          </div>
        </div>
        <button
          onClick={handleSendReport}
          disabled={emailSending}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          {emailSending ? 'Sending...' : '📧 Send Report Now'}
        </button>
        {emailMessage && (
          <div style={{
            marginTop: '12px',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            backgroundColor: emailMessage.type === 'success' ? '#e8f5e9' : '#fce4ec',
            color: emailMessage.type === 'success' ? '#2e7d32' : '#c62828'
          }}>
            {emailMessage.text}
          </div>
        )}
      </div>

      {/* Stats placeholder */}
      <div className="card" style={{ marginTop: '32px' }}>
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '20px',
          color: '#333'
        }}>
          Quick Stats
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f84f1' }}>--</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Total MRs</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#4CAF50' }}>--</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Total Doctors</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#FF9800' }}>--</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Today's Profiles</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
